import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useScreenWakeLock } from "../hooks/useScreenWakeLock";
import {
  clearFloorToken,
  clearShopFloorSessionId,
  configureShopMode,
  getShopFloorSessionId,
  setFloorToken,
  setShopFloorSessionId,
} from "../api/apiClient";
import { shopModeApi, type FloorActor } from "../api/shopMode";
import { t } from "../i18n/t";
import { errorText } from "../i18n/serverErrors";

/** 2-minute client-side idle window for floor workers (mirrors FLOOR_SESSION_IDLE_SECONDS). */
const FLOOR_IDLE_MS = 2 * 60 * 1000;
/**
 * G-85: functions, not constants — the text is looked up when the message is
 * needed, so it comes out in the kiosk's language rather than in whatever
 * language was active when this module first loaded (English, always).
 */
const shopSessionLostMessage = () => t("session.lost");
const shopSessionStorageMessage = () => t("session.storage");

/**
 * A correct PIN that outranks whoever armed the tablet (owner ruling
 * 2026-08-03). Matched on the server's structured `code`, never on the
 * message, so rewording the copy cannot break the behaviour.
 */
function isOverCeilingError(error: unknown): boolean {
  const body = (error as { body?: { code?: unknown } } | null)?.body;
  return body?.code === "PIN_OVER_CEILING";
}

function isShopSessionError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return /missing x-shop-floor-session-id|shop floor session.*(?:invalid|inactive|expired|not found)|floor session.*(?:invalid|inactive|expired)/i.test(
    error.message,
  );
}

// ── Context shape ─────────────────────────────────────────────────────────────

interface ShopModeContextValue {
  // ── Tablet (armed-session) state ──────────────────────────────────────────
  /** True once a ShopFloorSession is active and stored in sessionStorage. */
  isArmed: boolean;
  /** UUID from sessionStorage — the tablet's credential for SFS-only routes. */
  shopFloorSessionId: string | null;
  locationId: string | null;
  sessionExpiresAt: Date | null;
  /**
   * False until the initial status check (GET /shop-mode/status or no SFS found)
   * has resolved. Prevents a flash of the wrong UI on page reload.
   */
  sessionChecked: boolean;
  /** Shown after an armed session is lost so the user knows how to recover. */
  sessionRecoveryMessage: string | null;

  // ── Floor worker (pin-session) state ─────────────────────────────────────
  /** True while a valid JWT₃ is held in memory (worker is authenticated). */
  isFloorAuthenticated: boolean;
  floorActor: FloorActor | null;
  /** Hard expiry of the current JWT₃ (now+10 min from pin/verify). */
  floorTokenExpiresAt: Date | null;

  // ── Screen ────────────────────────────────────────────────────────────────
  /**
   * Whether a screen wake lock is actually held (owner, 2026-09-13: the kiosk
   * screen must not sleep). OBSERVED, not assumed — the platform can refuse,
   * and a shop that thinks its tablet is awake when it is not is worse off
   * than one that is told plainly.
   */
  screenAwake: { supported: boolean; held: boolean };

  // ── Actions ───────────────────────────────────────────────────────────────
  /** Arm the tablet. Manager: locationId ignored (uses assignment). Owner/Admin: required. */
  enter: (locationId?: string) => Promise<void>;
  /** Disarm the tablet and revoke the ShopFloorSession. */
  exit: () => Promise<void>;
  /** Verify a worker's PIN and store the resulting JWT₃ in memory. */
  verifyPin: (pin: string) => Promise<void>;
  /** G-48: the same, from a fob tap. `counter` rides along when the tag has one. */
  verifyFob: (payload: string, counter?: number) => Promise<void>;
  /**
   * Revoke the current ShopPinSession (idle lock).
   * Called by the idle timer and by explicit "lock" UI actions.
   */
  lockPin: () => Promise<void>;
}

const ShopModeContext = createContext<ShopModeContextValue | null>(null);

// ── Provider ─────────────────────────────────────────────────────────────────

export function ShopModeProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();

  // Armed-session state
  const [isArmed, setIsArmed] = useState(false);
  const [shopFloorSessionId, setShopFloorSessionIdState] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<string | null>(null);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<Date | null>(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [sessionRecoveryMessage, setSessionRecoveryMessage] = useState<string | null>(null);

  // Floor-worker state
  const [floorActor, setFloorActor] = useState<FloorActor | null>(null);
  const [floorTokenExpiresAt, setFloorTokenExpiresAt] = useState<Date | null>(null);

  /*
   * The screen stays awake for the whole ARMED period — the locked PIN screen
   * as much as the floor screens, because "kiosk mode" is the whole session and
   * the lock screen is the one a passer-by is most likely to be looking at.
   * Released automatically when the tablet is disarmed, so a phone or a desk
   * browser that never arms never holds one.
   */
  const screenAwake = useScreenWakeLock(isArmed);

  // Timers
  const idleTimerRef = useRef<number | null>(null);
  const hardCapTimerRef = useRef<number | null>(null);

  // ── Register navigateToShopMode with apiClient ────────────────────────────
  // When the Manager's CRM JWT expires mid-session while the tablet is armed,
  // the 401 interceptor in request() calls this instead of navigating to /login.
  useEffect(() => {
    configureShopMode({
      navigateToShopMode: () => navigate("/app/in-the-shop", { replace: true }),
    });
  }, [navigate]);

  // ── Clear floor-worker state (does NOT touch the armed session) ───────────
  const clearFloorState = useCallback(() => {
    clearFloorToken();
    setFloorActor(null);
    setFloorTokenExpiresAt(null);
    if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
    if (hardCapTimerRef.current) window.clearTimeout(hardCapTimerRef.current);
    idleTimerRef.current = null;
    hardCapTimerRef.current = null;
  }, []);

  // ── Clear the complete shop-mode session in one place ────────────────────
  const resetArmedSession = useCallback((message: string | null = null) => {
    clearShopFloorSessionId();
    setShopFloorSessionIdState(null);
    setIsArmed(false);
    setLocationId(null);
    setSessionExpiresAt(null);
    setSessionRecoveryMessage(message);
    clearFloorState();
  }, [clearFloorState]);

  // ── Restore session on page load ──────────────────────────────────────────
  // Check sessionStorage; if an SFS exists, verify it's still ACTIVE via the API.
  // Floor token is in memory only — worker must re-enter PIN after a reload.
  useEffect(() => {
    const sfsId = getShopFloorSessionId();
    if (!sfsId) {
      setSessionChecked(true);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const status = await shopModeApi.status(sfsId);
        if (!cancelled && status.status === "ACTIVE") {
          setSessionRecoveryMessage(null);
          setIsArmed(true);
          setShopFloorSessionIdState(sfsId);
          setLocationId(status.locationId);
          setSessionExpiresAt(new Date(status.expiresAt));
        } else if (!cancelled) {
          // Session expired or revoked server-side — clean up locally.
          resetArmedSession(shopSessionLostMessage());
        }
      } catch {
        if (!cancelled) resetArmedSession(shopSessionLostMessage());
      } finally {
        if (!cancelled) setSessionChecked(true);
      }
    })();

    return () => { cancelled = true; };
  }, [resetArmedSession]);

  // ── Floor-worker idle timer ───────────────────────────────────────────────
  // While a floor worker is authenticated, any user activity resets the 2-min
  // idle timer. When it fires, call lockPin() to revoke the server-side session
  // and clear local floor state — mirroring the server's FLOOR_SESSION_IDLE_SECONDS.
  const armIdleTimer = useCallback(() => {
    if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
    idleTimerRef.current = window.setTimeout(async () => {
      try { await shopModeApi.lockPin(); } catch { /* JWT₃ may already be expired */ }
      clearFloorState();
    }, FLOOR_IDLE_MS);
  }, [clearFloorState]);

  useEffect(() => {
    if (!floorActor) return;

    armIdleTimer();

    const events: (keyof WindowEventMap)[] = [
      "mousemove",
      "keydown",
      "click",
      "touchstart",
      "scroll",
    ];
    const handler = () => armIdleTimer();
    for (const evt of events) {
      window.addEventListener(evt, handler, { passive: true });
    }
    return () => {
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
      for (const evt of events) window.removeEventListener(evt, handler);
    };
  }, [floorActor, armIdleTimer]);

  // ── JWT₃ hard-cap timer ───────────────────────────────────────────────────
  // The JWT₃ is cryptographically expired at floorTokenExpiresAt regardless of
  // activity.  Clear floor state at that moment so the UI prompts for PIN again.
  useEffect(() => {
    if (!floorTokenExpiresAt) return;
    const ms = Math.max(0, floorTokenExpiresAt.getTime() - Date.now());
    if (ms <= 0) { clearFloorState(); return; }
    hardCapTimerRef.current = window.setTimeout(clearFloorState, ms);
    return () => {
      if (hardCapTimerRef.current) window.clearTimeout(hardCapTimerRef.current);
    };
  }, [floorTokenExpiresAt, clearFloorState]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const enter = useCallback(async (newLocationId?: string) => {
    setSessionRecoveryMessage(null);
    const result = await shopModeApi.enter({ locationId: newLocationId });
    const persisted = setShopFloorSessionId(result.shopFloorSessionId);
    if (!persisted) {
      // The server session exists, but the browser cannot retain its credential.
      // Revoke it immediately rather than presenting an armed UI that cannot work.
      try {
        await shopModeApi.exit(result.shopFloorSessionId);
      } catch {
        // Best effort: the local UI must still fail closed.
      }
      resetArmedSession(shopSessionStorageMessage());
      throw new Error(shopSessionStorageMessage());
    }

    setShopFloorSessionIdState(result.shopFloorSessionId);   // React state
    setIsArmed(true);
    setLocationId(result.locationId);
    setSessionExpiresAt(new Date(result.expiresAt));
  }, [resetArmedSession]);

  const exit = useCallback(async () => {
    if (!shopFloorSessionId) {
      resetArmedSession(shopSessionLostMessage());
      return;
    }

    try {
      await shopModeApi.exit(shopFloorSessionId);
      resetArmedSession();
    } catch (error) {
      if (isShopSessionError(error)) {
        resetArmedSession(shopSessionLostMessage());
        return;
      }
      throw error;
    }
  }, [resetArmedSession, shopFloorSessionId]);

  /**
   * G-48: PIN and fob differ only in WHICH call mints the floor token. The
   * ceiling handling and the lost-session handling below are identical for
   * both and must stay that way, so they live here once and the two doors are
   * thin wrappers.
   */
  const acceptFloorCredential = useCallback(async (
    mint: (sessionId: string) => Promise<{
      token: string;
      actor: FloorActor;
      expiresAt: string;
    }>,
  ) => {
    const persistedSessionId = getShopFloorSessionId();
    if (!shopFloorSessionId || persistedSessionId !== shopFloorSessionId) {
      resetArmedSession(shopSessionLostMessage());
      throw new Error(shopSessionLostMessage());
    }

    try {
      const result = await mint(shopFloorSessionId);
      setFloorToken(result.token);                        // module state — used by floorRequest()
      setFloorActor(result.actor);
      setFloorTokenExpiresAt(new Date(result.expiresAt));
    } catch (error) {
      // The PIN outranked the tablet's ceiling: the SERVER has already revoked
      // the floor session, so the tablet must stop believing it is armed —
      // otherwise the next action fails with a confusing "session expired".
      // The server's own wording is kept: it explains what to do next, which
      // the generic lost-session message does not.
      if (isOverCeilingError(error)) {
        // S5d: still the server's OWN wording, now in the florist's language.
        // `errorText` matches on the `PIN_OVER_CEILING` code rather than the
        // sentence, so rewording the English cannot break it.
        const message = errorText(error, "session.lost");
        resetArmedSession(message);
        throw new Error(message);
      }
      if (isShopSessionError(error)) {
        resetArmedSession(shopSessionLostMessage());
        throw new Error(shopSessionLostMessage());
      }
      throw error;
    }
  }, [resetArmedSession, shopFloorSessionId]);

  const verifyPin = useCallback(
    (pin: string) =>
      acceptFloorCredential((sessionId) => shopModeApi.verifyPin(pin, sessionId)),
    [acceptFloorCredential],
  );

  /**
   * A fob tap. Same outcome as a PIN, including the shift start — the server
   * treats them as one event (OD-36).
   */
  const verifyFob = useCallback(
    (payload: string, counter?: number) =>
      acceptFloorCredential((sessionId) =>
        shopModeApi.verifyFob(payload, counter, sessionId),
      ),
    [acceptFloorCredential],
  );

  const lockPin = useCallback(async () => {
    try { await shopModeApi.lockPin(); } catch { /* idempotent — ignore expired JWT₃ */ }
    clearFloorState();
  }, [clearFloorState]);

  // ── Context value ─────────────────────────────────────────────────────────

  const value = useMemo<ShopModeContextValue>(
    () => ({
      isArmed,
      shopFloorSessionId,
      locationId,
      sessionExpiresAt,
      sessionChecked,
      sessionRecoveryMessage,
      isFloorAuthenticated: floorActor !== null,
      floorActor,
      floorTokenExpiresAt,
      screenAwake,
      enter,
      exit,
      verifyPin,
      verifyFob,
      lockPin,
    }),
    [
      isArmed,
      shopFloorSessionId,
      locationId,
      sessionExpiresAt,
      sessionChecked,
      sessionRecoveryMessage,
      floorActor,
      floorTokenExpiresAt,
      screenAwake,
      enter,
      exit,
      verifyPin,
      verifyFob,
      lockPin,
    ],
  );

  return (
    <ShopModeContext.Provider value={value}>{children}</ShopModeContext.Provider>
  );
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useShopMode() {
  const ctx = useContext(ShopModeContext);
  if (!ctx) {
    throw new Error("useShopMode must be used inside <ShopModeProvider>");
  }
  return ctx;
}
